
import { createContext, useContext, ReactNode } from 'react';
import { useAppData } from '@/hooks/useAppData';
import { 
  Employee, 
  DashboardStats,
  User,
  Department,
  Position,
  Loan
} from '@/types';

// Define the context type
interface AppDataContextType {
  data: {
    employees: Employee[];
    departments: Department[];
    positions: Position[];
    dashboardStats: DashboardStats | null;
    users: User[];
    loans: Loan[];
  };
  loading: boolean;
  refresh: {
    all: () => Promise<void>;
    employees: () => Promise<Employee[] | null>;
    departments: () => Promise<Department[] | null>;
    positions: () => Promise<Position[] | null>;
    users: () => Promise<User[] | null>;
    dashboardStats: () => Promise<DashboardStats | null>;
    loans: () => Promise<Loan[] | null>;
  };
}

// Create context with default values
const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

// Provider component
export function AppDataProvider({ children }: { children: ReactNode }) {
  const appData = useAppData();
  
  return (
    <AppDataContext.Provider value={appData as AppDataContextType}>
      {children}
    </AppDataContext.Provider>
  );
}

// Custom hook for using the app data context
export function useAppDataContext() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppDataContext must be used within an AppDataProvider');
  }
  return context;
}
